import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { MessageSquare, Bug, Send, CheckCircle2 } from "lucide-react";
import PublicLayout from "@/components/PublicLayout";
import { supportEmail } from "@/lib/companyBrand";

const categories = {
  feedback: ["General Feedback", "Feature Request", "UI/UX Improvement", "Other"],
  bug: ["Recording Issue", "Login/Auth Issue", "Display/UI Bug", "Performance Issue", "Other"],
};

const FeedbackPage = () => {
  const [searchParams] = useSearchParams();
  const isBug = searchParams.get("type") === "bug";
  const [mode, setMode] = useState(isBug ? "bug" : "feedback");
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", category: "", message: "" });

  const handleSubmit = (e) => {
    e.preventDefault();
    const subject = encodeURIComponent(
      `[${mode === "bug" ? "Bug Report" : "Feedback"}] ${form.category || "General"}`
    );
    const body = encodeURIComponent(
      `Name: ${form.name}\nEmail: ${form.email}\nCategory: ${form.category}\nType: ${mode === "bug" ? "Bug Report" : "Feedback"}\n\n${form.message}`
    );
    window.location.href = `mailto:${supportEmail()}?subject=${subject}&body=${body}`;
    setSubmitted(true);
  };

  const update = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  if (submitted) {
    return (
      <PublicLayout>
        <main className="relative z-10 px-4 md:px-8 pt-20 md:pt-32 pb-20 md:pb-32 max-w-lg mx-auto text-center safe-area-padding">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <div className="w-14 h-14 bg-emerald-500/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-7 h-7 text-emerald-500" />
            </div>
            <h1 className="text-2xl md:text-3xl font-light tracking-tight mb-3" style={{ fontFamily: "Outfit, sans-serif" }}>
              Thank you!
            </h1>
            <p className="text-zinc-400 text-sm mb-6">
              Your email client should have opened with your {mode === "bug" ? "bug report" : "feedback"} pre-filled. If it didn't, you can reach us directly at{" "}
              <a href={`mailto:${supportEmail()}`} className="text-[#f4cf08] hover:underline">{supportEmail()}</a>.
            </p>
            <button
              onClick={() => { setSubmitted(false); setForm({ name: "", email: "", category: "", message: "" }); }}
              className="text-sm text-zinc-500 hover:text-white transition-colors"
            >
              Submit another
            </button>
          </motion.div>
        </main>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <main className="relative z-10 px-4 md:px-8 pt-12 md:pt-20 pb-20 md:pb-32 max-w-lg mx-auto safe-area-padding">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
          <h1
            className="text-3xl md:text-4xl font-light tracking-tight text-center mb-3"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            {mode === "bug" ? "Report a Bug" : "Submit Feedback"}
          </h1>
          <p className="text-sm text-zinc-400 text-center mb-8">
            {mode === "bug"
              ? "Found something that isn't working? Let us know and we'll fix it."
              : "Have an idea or suggestion? We'd love to hear from you."}
          </p>

          {/* Toggle */}
          <div className="flex items-center justify-center gap-2 mb-8">
            <button
              onClick={() => setMode("feedback")}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                mode === "feedback" ? "bg-white/10 text-white" : "text-zinc-500 hover:text-zinc-300"
              }`}
            >
              <MessageSquare className="w-4 h-4" /> Feedback
            </button>
            <button
              onClick={() => setMode("bug")}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                mode === "bug" ? "bg-white/10 text-white" : "text-zinc-500 hover:text-zinc-300"
              }`}
            >
              <Bug className="w-4 h-4" /> Bug Report
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs text-zinc-500 mb-1.5">Name</label>
              <input
                type="text"
                required
                value={form.name}
                onChange={update("name")}
                className="w-full bg-zinc-900/50 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder-zinc-600 focus:outline-hidden focus:border-[#f4cf08]/30 transition-colors"
                placeholder="Your name"
              />
            </div>
            <div>
              <label className="block text-xs text-zinc-500 mb-1.5">Email</label>
              <input
                type="email"
                required
                value={form.email}
                onChange={update("email")}
                className="w-full bg-zinc-900/50 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder-zinc-600 focus:outline-hidden focus:border-[#f4cf08]/30 transition-colors"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <label className="block text-xs text-zinc-500 mb-1.5">Category</label>
              <select
                required
                value={form.category}
                onChange={update("category")}
                className="w-full bg-zinc-900/50 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-hidden focus:border-[#f4cf08]/30 transition-colors appearance-none"
              >
                <option value="" disabled>Select a category</option>
                {categories[mode].map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-zinc-500 mb-1.5">Message</label>
              <textarea
                required
                rows={5}
                value={form.message}
                onChange={update("message")}
                className="w-full bg-zinc-900/50 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder-zinc-600 focus:outline-hidden focus:border-[#f4cf08]/30 transition-colors resize-none"
                placeholder={mode === "bug" ? "Describe the bug: what happened, what you expected, and steps to reproduce..." : "Tell us your idea or suggestion..."}
              />
            </div>
            <button
              type="submit"
              className="w-full flex items-center justify-center gap-2 bg-[#f4cf08] text-black font-medium rounded-full px-6 py-3 text-sm
                         hover:scale-[1.02] active:scale-95 transition-transform shadow-[0_0_40px_rgba(244,207,8,0.25)]"
            >
              <Send className="w-4 h-4" /> Send {mode === "bug" ? "Bug Report" : "Feedback"}
            </button>
          </form>
        </motion.div>
      </main>
    </PublicLayout>
  );
};

export default FeedbackPage;
